import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { ContactProvider } from "@/components/ContactModal";
import ErrorBoundary from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Calendar from "@/pages/Calendar";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Services from "@/pages/Services";
import AIMessages from "@/pages/AIMessages";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Staff from "@/pages/Staff";
import Timelister from "@/pages/Timelister";
import PublicBooking from "@/pages/PublicBooking";
import CancelBooking from "@/pages/CancelBooking";
import AppLayout from "@/pages/AppLayout";
import Vilkar from "@/pages/Vilkar";
import Personvern from "@/pages/Personvern";
import Foresporsler from "@/pages/Foresporsler";
import AdminBusinesses from "@/pages/AdminBusinesses";
import ActivateInvitation from "@/pages/ActivateInvitation";
import SeoLandingPage from "@/pages/seo/SeoLandingPage";
import { SEO_LANDING_PAGES } from "@/pages/seo/content";

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/logg-inn" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/logg-inn" replace />;
  if (user.role !== "owner") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
        <ContactProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/logg-inn" element={<Login />} />
            <Route path="/opprett-konto" element={<Navigate to="/logg-inn" replace />} />
            <Route path="/glemt-passord" element={<ForgotPassword />} />
            <Route path="/aktiver/:token" element={<ActivateInvitation />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/avbestill/:token" element={<CancelBooking />} />
            <Route path="/vilkar" element={<Vilkar />} />
            <Route path="/personvern" element={<Personvern />} />

            {/* SEO landing pages — public, marketing-only, never gated. */}
            {Object.entries(SEO_LANDING_PAGES).map(([slug, content]) => (
              <Route
                key={slug}
                path={content.path}
                element={<SeoLandingPage content={content} />}
              />
            ))}

            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/kalender" element={<Calendar />} />
              <Route path="/kunder" element={<Customers />} />
              <Route path="/kunder/:id" element={<CustomerDetail />} />
              <Route path="/tjenester" element={<Services />} />
              <Route path="/ai-meldinger" element={<AIMessages />} />
              <Route path="/timelister" element={<Timelister />} />
              <Route path="/rapporter" element={<OwnerOnly><Reports /></OwnerOnly>} />
              <Route path="/personal" element={<OwnerOnly><Staff /></OwnerOnly>} />
              <Route path="/innstillinger" element={<OwnerOnly><Settings /></OwnerOnly>} />
              <Route path="/admin/leads" element={<OwnerOnly><Foresporsler /></OwnerOnly>} />
              <Route path="/admin/bedrifter" element={<OwnerOnly><AdminBusinesses /></OwnerOnly>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        </ContactProvider>
      </AuthProvider>
      </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
